import { createContext, useContext, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import type { ProcessedFormField } from "@/types/api/formSchema";
import {
  useAttachmentUpload,
  type UploadResult,
} from "../hooks/useAttachmentUpload";
import { AttachmentPreviewLink } from "../components/AttachmentPreviewLink";
import { resolveAttachmentTemplate } from "../utils/attachmentTemplates";

export type AttachmentValue = {
  attachment_id: string;
  file_name: string;
};

type AttachmentOwner = {
  ownerRef?: string;
  ownerType?: string;
  authMode?: "vendor" | "reviewer";
};

export const AttachmentOwnerContext = createContext<AttachmentOwner>({});

const DEFAULT_ACCEPT = ".pdf,image/png,image/jpeg";
const DEFAULT_MAX_SIZE_BYTES = 15 * 1024 * 1024;

function parseAcceptToMatcher(accept: string): (file: File) => boolean {
  const parts = accept
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const extensions = parts.filter((p) => p.startsWith("."));
  const mimeGlobs = parts.filter((p) => p.includes("/"));
  return (file) => {
    const name = file.name.toLowerCase();
    const type = (file.type || "").toLowerCase();
    for (const ext of extensions) {
      if (name.endsWith(ext.toLowerCase())) return true;
    }
    for (const glob of mimeGlobs) {
      if (glob.endsWith("/*")) {
        if (type.startsWith(glob.slice(0, -1))) return true;
      } else if (type === glob.toLowerCase()) {
        return true;
      }
    }
    return false;
  };
}

function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type Props = {
  field: ProcessedFormField & { accept?: string; maxSize?: number };
  value: AttachmentValue | null | "";
  onChange: (value: AttachmentValue | null) => void;
  onBlur: () => void;
  name: string;
};

export function AttachmentField({
  field: schemaField,
  value,
  onChange,
  onBlur,
  name,
}: Props) {
  const { ownerRef, ownerType, authMode } = useContext(AttachmentOwnerContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const { status, error, upload, reset, cancel } = useAttachmentUpload();
  const [clientError, setClientError] = useState<string | null>(null);

  const uploaded: AttachmentValue | null =
    value && typeof value === "object" && value.attachment_id ? value : null;
  const busy =
    status === "generating" || status === "uploading" || status === "confirming";

  const acceptString = schemaField.accept ?? DEFAULT_ACCEPT;
  const maxSize = schemaField.maxSize ?? DEFAULT_MAX_SIZE_BYTES;
  const acceptMatcher = parseAcceptToMatcher(acceptString);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (inputRef.current) inputRef.current.value = "";

    setClientError(null);

    if (file.size > maxSize) {
      setClientError(
        `File is ${formatMB(file.size)}; max allowed is ${formatMB(maxSize)}.`,
      );
      return;
    }
    if (!acceptMatcher(file)) {
      setClientError(
        `File type not accepted. Allowed: ${acceptString.replace(/,/g, ", ")}.`,
      );
      return;
    }

    const result: UploadResult | null = await upload(
      file,
      ownerRef,
      ownerType,
      authMode,
    );
    if (result) {
      onChange({
        attachment_id: result.attachment_id,
        file_name: result.file_name,
      });
    }
    onBlur();
  };

  const handleReplace = () => {
    cancel();
    setClientError(null);
    inputRef.current?.click();
  };

  const handleRemove = () => {
    cancel();
    setClientError(null);
    onChange(null);
    onBlur();
  };

  const statusLabel = (() => {
    switch (status) {
      case "generating":
        return "Preparing upload…";
      case "uploading":
        return "Uploading…";
      case "confirming":
        return "Finalising…";
      default:
        return null;
    }
  })();

  const shownError = clientError ?? error;
  const template = resolveAttachmentTemplate(name);

  return (
    <Box>
      <input
        ref={inputRef}
        id={name}
        name={name}
        type="file"
        accept={acceptString}
        disabled={!!schemaField.disabled || busy}
        onChange={handleFile}
        style={{ display: "none" }}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        {uploaded ? (
          <>
            <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <AttachmentPreviewLink
                attachmentId={uploaded.attachment_id}
                fileName={uploaded.file_name}
                authMode={authMode}
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              disabled={!!schemaField.disabled || busy}
              onClick={handleReplace}
            >
              Replace
            </Button>
            <Button
              size="small"
              variant="text"
              color="error"
              disabled={!!schemaField.disabled || busy}
              onClick={handleRemove}
            >
              Remove
            </Button>
          </>
        ) : busy ? (
          <>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              {statusLabel}
            </Typography>
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={handleRemove}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <RadioButtonUncheckedIcon
              sx={{ color: "text.disabled", fontSize: 20 }}
            />
            <Typography sx={{ fontSize: 14, color: "text.secondary", flex: 1 }}>
              Not uploaded
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={!!schemaField.disabled}
              onClick={() => {
                reset();
                setClientError(null);
                inputRef.current?.click();
              }}
            >
              Choose file
            </Button>
          </>
        )}
      </Stack>

      {shownError ? (
        <Typography
          sx={{ fontSize: 12, color: "error.main", mt: 0.75 }}
          role="alert"
        >
          {shownError}
        </Typography>
      ) : null}

      {template ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
          {template.href ? (
            <Link
              href={template.href}
              download
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 12 }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
              {template.label}
            </Link>
          ) : (
            <>
              <Button
                size="small"
                variant="text"
                disabled
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{ fontSize: 12, textTransform: "none", p: 0, minWidth: 0 }}
              >
                {template.label}
              </Button>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Template will be available soon
              </Typography>
            </>
          )}
        </Stack>
      ) : null}
    </Box>
  );
}
